import { useMapsLibrary } from '@vis.gl/react-google-maps';
import React from 'react';
import { useDebounce } from 'use-debounce';

export interface AddressSuggestion {
    id: string;
    label: string;
    prediction: google.maps.places.PlacePrediction;
}

export interface SelectedPlace {
    address: string;
    latitude: number;
    longitude: number;
    zipcode?: string;
}

export const usePlacesAutocomplete = (input: string, regionCode?: string) => {
    const places = useMapsLibrary('places');
    const [suggestions, setSuggestions] = React.useState<Array<AddressSuggestion>>([]);
    const [ready, setReady] = React.useState(false);
    const sessionTokenRef = React.useRef<google.maps.places.AutocompleteSessionToken>();
    const skipQueryRef = React.useRef<string>();
    const [debouncedInput] = useDebounce(input, 300);

    React.useEffect(() => {
        const query = debouncedInput.trim();

        if (!places || !query || query === skipQueryRef.current) {
            setSuggestions([]);
            setReady(false);
            return;
        }

        let cancelled = false;
        setReady(false);

        const search = async () => {
            if (!sessionTokenRef.current) {
                sessionTokenRef.current = new places.AutocompleteSessionToken();
            }

            const request: google.maps.places.AutocompleteRequest = {
                input: query,
                sessionToken: sessionTokenRef.current
            };

            if (regionCode) {
                request.includedRegionCodes = [regionCode];
            }

            const { suggestions: results } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

            if (cancelled) return;

            setSuggestions(
                (results ?? [])
                    .map(result => result.placePrediction)
                    .filter((prediction): prediction is google.maps.places.PlacePrediction => !!prediction)
                    .map(prediction => ({
                        id: prediction.placeId,
                        label: prediction.text.toString(),
                        prediction
                    }))
            );
            setReady(true);
        };

        search().catch(() => {
            if (cancelled) return;
            setSuggestions([]);
            setReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [places, debouncedInput, regionCode]);

    const selectSuggestion = async (suggestion: AddressSuggestion): Promise<SelectedPlace | undefined> => {
        const place = suggestion.prediction.toPlace();
        await place.fetchFields({ fields: ['location', 'addressComponents'] });
        sessionTokenRef.current = undefined;
        skipQueryRef.current = suggestion.label;
        setSuggestions([]);

        const location = place.location;
        if (!location) return undefined;

        return {
            address: suggestion.label,
            latitude: location.lat(),
            longitude: location.lng(),
            zipcode:
                place.addressComponents?.find(component => component.types.includes('postal_code'))?.longText ??
                undefined
        };
    };

    const clearSkip = () => {
        skipQueryRef.current = undefined;
    };

    return { suggestions, ready, selectSuggestion, clearSkip };
};
